/**
 * audit-implementation command — uses the built-in Implementation Auditor agent
 * to verify an LLM command integration follows the agent-contracts stack.
 *
 * The agent is read-only: it analyzes but does not modify files.
 */

import { existsSync } from "node:fs";
import { resolve } from "node:path";

import { runWorkflow } from "./workflow-runner.js";
import { buildTaskPrompt } from "./task-runner.js";
import type { SdkAdapter, TaskContext } from "./task-runner.js";
import type { WorkflowInvocation } from "./workflow-runner.js";
import {
  agentRegistry,
  taskRegistry,
  handoffSchemas,
  workflowRegistry,
  resolvedDsl,
} from "../builtin-agents/generated/index.js";
import {
  createGuardrailHooksFromRules,
  type GuardrailHooks,
  type GuardrailRuleData,
} from "./guardrail-hooks.js";
import type { AuditResult } from "../builtin-agents/generated/handoffs.js";

// ---------------------------------------------------------------------------
// Locale detection
// ---------------------------------------------------------------------------

function getLocale(): string {
  return (
    process.env.LANG?.split(".")[0] ??
    process.env.LC_ALL?.split(".")[0] ??
    process.env.LANGUAGE?.split(":")[0] ??
    "en_US"
  );
}

function isJapanese(): boolean {
  return getLocale().startsWith("ja");
}

// ---------------------------------------------------------------------------
// Messages — locale-aware
// ---------------------------------------------------------------------------

interface Messages {
  noProjectDir: (dir: string) => string;
  adapterError: (msg: string) => string;
  dryRunHeader: string;
  dryRunProjectDir: (dir: string) => string;
  dryRunFocus: (focus: string) => string;
  auditStarting: (adapter: string) => string;
  auditSuccess: string;
  auditFailed: (reason: string) => string;
  riskLevel: (level: string) => string;
  findingsHeader: (count: number) => string;
  noFindings: string;
  recommendedActionsHeader: string;
}

const en: Messages = {
  noProjectDir: (dir) => `Project directory not found: ${dir}`,
  adapterError: (msg) => `Adapter initialization failed: ${msg}`,
  dryRunHeader: "=== Dry Run (audit-implementation) ===",
  dryRunProjectDir: (dir) => `Project: ${dir}`,
  dryRunFocus: (focus) => `Focus: ${focus}`,
  auditStarting: (adapter) => `Starting audit with ${adapter} adapter...`,
  auditSuccess: "Audit completed.",
  auditFailed: (reason) => `Audit failed: ${reason}`,
  riskLevel: (level) => `Risk level: ${level}`,
  findingsHeader: (count) => `Findings (${count}):`,
  noFindings: "No findings.",
  recommendedActionsHeader: "Recommended actions:",
};

const ja: Messages = {
  noProjectDir: (dir) => `プロジェクトディレクトリが見つかりません: ${dir}`,
  adapterError: (msg) => `アダプター初期化に失敗しました: ${msg}`,
  dryRunHeader: "=== ドライラン (audit-implementation) ===",
  dryRunProjectDir: (dir) => `プロジェクト: ${dir}`,
  dryRunFocus: (focus) => `フォーカス: ${focus}`,
  auditStarting: (adapter) => `${adapter} アダプターで監査を開始します...`,
  auditSuccess: "監査が完了しました。",
  auditFailed: (reason) => `監査に失敗しました: ${reason}`,
  riskLevel: (level) => `リスクレベル: ${level}`,
  findingsHeader: (count) => `検出事項 (${count}件):`,
  noFindings: "検出事項はありません。",
  recommendedActionsHeader: "推奨アクション:",
};

function msg(): Messages {
  return isJapanese() ? ja : en;
}

// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------

async function createAuditAdapter(
  adapterName: string,
  projectDir: string,
  model?: string,
  guardrailHooks?: GuardrailHooks,
): Promise<SdkAdapter> {
  switch (adapterName) {
    case "claude": {
      const { ClaudeAgentSdkAdapter } = await import("../adapters/claude-agent-sdk.js");
      return new ClaudeAgentSdkAdapter({
        cwd: projectDir,
        model,
        guardrailHooks,
      });
    }
    case "mock": {
      const { MockAdapter } = await import("../adapters/mock.js");
      return new MockAdapter();
    }
    default:
      throw new Error(
        `Unsupported adapter for audit-implementation: "${adapterName}". ` +
        `Available: claude, mock`,
      );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface AuditOptions {
  adapter: string;
  projectDir: string;
  focus: "all" | "dsl" | "runtime" | "cli-contract" | "architecture";
  dryRun: boolean;
  failOn: "warning" | "error" | "critical";
  reportFormat: "json" | "text" | "yaml";
  model?: string;
  progressOutput?: { write(chunk: string): unknown };
}

export interface AuditRunResult {
  status: "success" | "failed" | "dry_run";
  exitCode: number;
  data?: AuditResult;
  error?: string;
}

const SEVERITY_ORDER: Record<string, number> = {
  info: 0,
  warning: 1,
  error: 2,
  critical: 3,
};

function hasFindings(result: AuditResult, failOn: string): boolean {
  const threshold = SEVERITY_ORDER[failOn] ?? 2;
  return result.findings.some(
    (f) => (SEVERITY_ORDER[f.severity] ?? 0) >= threshold,
  );
}

export async function runAuditImplementation(
  options: AuditOptions,
): Promise<AuditRunResult> {
  const m = msg();
  const projectDir = resolve(options.projectDir);

  if (!existsSync(projectDir)) {
    return { status: "failed", exitCode: 3, error: m.noProjectDir(projectDir) };
  }

  // --- Build invocation context (shared between dry-run and live run) ---
  const focusInstruction = options.focus !== "all"
    ? `\n\nFocus the audit on the "${options.focus}" layer only.`
    : "";

  const userRequest = [
    `Audit the LLM command integration in the following project.`,
    `Project directory: ${projectDir}`,
    focusInstruction,
  ].join("\n");

  const context: TaskContext = {
    user_request: userRequest,
    relevant_paths: [projectDir],
  };

  // --- Dry run: emit prompts for all parallel audit tasks ---
  if (options.dryRun) {
    const agent = agentRegistry["implementation-auditor"];
    const auditTasks = ["audit-dsl", "audit-runtime", "audit-cli-contract", "audit-architecture"];
    const prompts = auditTasks.map((taskId) => {
      const task = taskRegistry[taskId];
      if (!task) return `# [Task not found: ${taskId}]`;
      return buildTaskPrompt(agent, task, context, { handoffSchemas });
    });
    const separator = "\n\n" + "=".repeat(72) + "\n\n";
    const fullPrompt = prompts.join(separator);

    return {
      status: "dry_run",
      exitCode: 0,
      data: {
        summary: fullPrompt,
        riskLevel: "low",
        findings: [],
      },
    };
  }

  // --- Create adapter ---
  let guardrailHooks: GuardrailHooks | undefined;
  const rawRules = resolvedDsl._guardrailRules as GuardrailRuleData | undefined;
  if (
    rawRules &&
    (rawRules.commandRules?.length ||
      rawRules.fileRules?.length ||
      rawRules.contentRules?.length)
  ) {
    guardrailHooks = createGuardrailHooksFromRules(rawRules);
  }

  let adapter: SdkAdapter;
  try {
    adapter = await createAuditAdapter(
      options.adapter,
      projectDir,
      options.model,
      guardrailHooks,
    );
  } catch (err) {
    return { status: "failed", exitCode: 12, error: m.adapterError((err as Error).message) };
  }

  console.error(m.auditStarting(options.adapter));

  const registries = {
    workflowRegistry,
    taskRegistry,
    agentRegistry,
    handoffSchemas,
  };

  // --- Run workflow ---
  const invocation: WorkflowInvocation = {
    workflow: "audit-implementation",
    user_request: userRequest,
    runtime: {
      maxFollowUps: 2,
      maxRetries: 1,
    },
    hooks: {
      onGate: async () => true,
    },
    context: {
      cwd: projectDir,
    },
    progressOutput: options.progressOutput,
  };

  const result = await runWorkflow(adapter, invocation, registries);

  if (result.status !== "completed") {
    const reason = result.escalation_reason ?? result.error_message ?? result.status;
    return { status: "failed", exitCode: 1, error: m.auditFailed(reason) };
  }

  const lastStep = result.steps[result.steps.length - 1];
  const outcomeData = (lastStep?.outcome as Record<string, unknown>)?.data as
    | AuditResult
    | undefined;

  const auditData: AuditResult = outcomeData ?? {
    summary: m.auditSuccess,
    riskLevel: "low",
    findings: [],
  };

  const exitCode = hasFindings(auditData, options.failOn) ? 10 : 0;

  return {
    status: "success",
    exitCode,
    data: auditData,
  };
}

// ---------------------------------------------------------------------------
// Text formatter
// ---------------------------------------------------------------------------

const SEVERITY_ICON: Record<string, string> = {
  critical: "✗",
  error: "✗",
  warning: "!",
  info: "·",
};

export function formatAuditResult(result: AuditRunResult): string {
  const m = msg();

  if (result.status === "dry_run") {
    return result.data?.summary ?? m.dryRunHeader;
  }

  if (result.status === "failed") {
    return result.error ?? m.auditFailed("unknown");
  }

  const data = result.data;
  if (!data) return m.auditSuccess;

  const lines: string[] = [data.summary, ""];

  lines.push(m.riskLevel(data.riskLevel));
  lines.push("");

  if (data.findings.length === 0) {
    lines.push(m.noFindings);
  } else {
    lines.push(m.findingsHeader(data.findings.length));
    for (const f of data.findings) {
      const icon = SEVERITY_ICON[f.severity] ?? "·";
      const id = f.id ? `[${f.id}] ` : "";
      const target = f.target ? ` (${f.target})` : "";
      lines.push(`  ${icon} ${id}${f.severity.toUpperCase()}: ${f.message}${target}`);
      if (f.recommendation) {
        lines.push(`    → ${f.recommendation}`);
      }
    }
    lines.push("");
  }

  if (data.recommendedActions && data.recommendedActions.length > 0) {
    lines.push(m.recommendedActionsHeader);
    for (const a of data.recommendedActions) {
      const cmd = a.command ? ` — ${a.command}` : "";
      lines.push(`  [${a.kind}] ${a.title}${cmd}`);
    }
  }

  return lines.join("\n");
}
